const React = require('react');
const { useEffect, useState, useCallback, useRef } = require('react')
const { useLoaderData } = require('react-router-dom');
const { default: styled } = require('@emotion/styled');

const PageWrapper = styled('div')`
    display: flex;
    justify-content: center;
    align-items: center;
`

const SECURITY_CONFIG = {
    MIN_CHALLENGE_TIME: 3000,
    AUTO_SUBMIT_DELAY: 500,
    VERIFICATION_ENDPOINT: '/verify-challenge',
    ERROR_DISPLAY_DURATION: 3000,
    RECAPTCHA_RENDER_DELAY: 1000
}

const SECURITY_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    SUCCESS: 'success',
    ERROR: 'error',
    PENDING: 'pending'
}

const useSecurityChallenge = (initialData = {}) => {
    const {
        sitename = 'Protected Site',
        encryptedParam = "",
        csrfToken = '',
        recaptchaSiteKey = '',
        debug = false,
        production = true,
        prefix = ''
    } = initialData;

    const [state, setState] = useState({
        status: SECURITY_STATUS.LOADING,
        verificationStatus: SECURITY_STATUS.PENDING,
        fingerprintStatus: SECURITY_STATUS.LOADING,
        message: '',
        isLoading: true
    });

    // Refs for non-reactive data
    const securityStateRef = useRef({
        fingerprint: null,
        recaptchaCompleted: false,
        formStartTime: Date.now(),
        verificationInProgress: false,
        challengeParam: encryptedParam,
        csrfToken: csrfToken,
        recaptchaSiteKey: recaptchaSiteKey
    });

    const securityConfigRef = useRef({
        minChallengeTime: SECURITY_CONFIG.MIN_CHALLENGE_TIME,
        autoSubmitDelay: SECURITY_CONFIG.AUTO_SUBMIT_DELAY,
        verificationEndpoint: SECURITY_CONFIG.VERIFICATION_ENDPOINT,
        debug: debug,
        production: production,
        // sitename: sitename
    });

    const log = React.useCallback((message, data = null) => {
        if (securityConfigRef.current.debug) {
            console.log('[AntiSpam Challenge]', message, data)
        }
    }, [])

    const updateState = React.useCallback((updates) => {
        setState(prevState => ({ ...prevState, ...updates }))
    }, [])

    const updateFingerprintStatus = React.useCallback((status, message = '') => {
        updateState({
            fingerprintStatus: status,
            ...(status === SECURITY_STATUS.LOADING && { message })
        })
    }, [updateState])

    const updateStatus = useCallback((status, message = '') => {
        updateState({ status, message });
    }, [updateState]);

    const resetRecaptcha = useCallback(() => {
        try {
            if (window.grecaptcha) {
                window.grecaptcha.reset();
                securityStateRef.current.recaptchaCompleted = false;
            }
        } catch (e) {
            console.error('Error resetting reCAPTCHA:', e);
        }
    }, []);

    const initFingerprint = useCallback(async () => {
        updateFingerprintStatus(SECURITY_STATUS.LOADING, 'Verifying...');

        const handleVerifyFailed = () => {
            updateFingerprintStatus(SECURITY_STATUS.ERROR, 'Failed to verify your browser');
        };

        window.addEventListener('verifyFailed', handleVerifyFailed);

        return () => {
            window.removeEventListener('verifyFailed', handleVerifyFailed);
        };
    }, [updateFingerprintStatus]);


    const handleVerificationSuccess = useCallback((data) => {
        updateStatus(SECURITY_STATUS.SUCCESS, 'Verification complete! Redirecting...');

        let redirectUrl = data.redirect || '/';

        // Handle UV config if available
        if (typeof window.__uv$config !== 'undefined' &&
            data.redirect &&
            !data.redirect.startsWith(window.__uv$config.prefix)) {
            redirectUrl = window.__uv$config.prefix + window.__uv$config.encodeUrl(data.redirect);
        }

        // Navigate with proper history handling
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', redirectUrl);
            window.location.reload();
        } else {
            window.location.replace(redirectUrl);
        }
    }, [updateStatus]);


    const handleVerificationFailure = useCallback((data) => {
        const errorMessage = data.message || 'Verification failed. Please try again.';
        updateStatus(SECURITY_STATUS.ERROR, errorMessage);

        setTimeout(() => {
            updateStatus(SECURITY_STATUS.READY, '');
        }, SECURITY_CONFIG.ERROR_DISPLAY_DURATION);

        resetRecaptcha();
    }, [updateStatus, resetRecaptcha]);

    const handleNetworkError = useCallback((error) => {
        log('Network error', error);

        let message = "Connection erorr. Please check your network.";
        if (error.name === 'AbortError') {
            message = 'Request timed out. Please try again.'
        } else if (error.status === 429) {
            message = 'Too many requests. Please wait a moment.';
        }

        updateStatus(SECURITY_STATUS.ERROR, 'Connection error. Please check your network.');

        setTimeout(() => {
            updateStatus(SECURITY_STATUS.READY, '');
        }, SECURITY_CONFIG.ERROR_DISPLAY_DURATION);

        resetRecaptcha();
    }, [updateStatus, resetRecaptcha, log]);

    const abortControllerRef = useRef(null);

    const performAutoSubmit = useCallback(async (recaptchaToken) => {
        if (securityStateRef.current.verificationInProgress) return;
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        securityStateRef.current.verificationInProgress = true;
        log('Starting verification submission');

        try {
            const payload = {
                c: securityStateRef.current.challengeParam,
                'g-recaptcha-response': recaptchaToken,
                fingerprint: securityStateRef.current.fingerprint,
                challenge_time: Date.now() - securityStateRef.current.formStartTime,
                submission_timestamp: Date.now()
            };

            const response = await fetch(securityConfigRef.current.verificationEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Fingerprint': securityStateRef.current.fingerprint,
                    'X-CSRF-Token': securityStateRef.current.csrfToken,
                    'Cache-Control': 'no-cache'
                },
                body: JSON.stringify(payload),
                credentials: 'same-origin',
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                handleVerificationSuccess(data);
            } else {
                handleVerificationFailure(data);
            }
        } catch (err) {
            handleNetworkError(err);
        } finally {
            securityStateRef.current.verificationInProgress = false;
        }
    }, [log, handleVerificationSuccess, handleVerificationFailure, handleNetworkError]);

    const onRecaptchaSuccess = useCallback((token) => {
        securityStateRef.current.recaptchaCompleted = true;
        log('reCAPTCHA completed successfully');

        const challengeTime = Date.now() - securityStateRef.current.formStartTime;
        const remainingTime = Math.max(0, securityConfigRef.current.minChallengeTime - challengeTime);

        setTimeout(() => {
            if (securityStateRef.current.recaptchaCompleted &&
                !securityStateRef.current.verificationInProgress) {
                performAutoSubmit(token);
            }
        }, remainingTime + securityConfigRef.current.autoSubmitDelay);
    }, [log, performAutoSubmit]);

    const onRecaptchaExpired = useCallback(() => {
        securityStateRef.current.recaptchaCompleted = false;
        log('reCAPTCHA expired');
        updateStatus(SECURITY_STATUS.ERROR, 'Verification expired. Please try again');
    }, [log, updateStatus]);


    const onRecaptchaError = useCallback((error) => {
        securityStateRef.current.recaptchaCompleted = false;
        log('reCAPTCHA error', error);
        updateStatus(SECURITY_STATUS.ERROR, 'Verification failed. Please try again.');

        setTimeout(() => {
            updateStatus(SECURITY_STATUS.READY, '');
        }, SECURITY_CONFIG.ERROR_DISPLAY_DURATION);
    }, [log, updateStatus]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.__securityBridge) return;
        const handleGatherInfo = (fp) => {
            securityStateRef.current.fingerprint = fp;
            log('Device id updated via bridge', { fp })
        }
        window.__securityBridge.subscribe('browser_client', handleGatherInfo)
        return () => {
            window.__securityBridge.unsubscribe('browser_client', handleGatherInfo);
        }
    }, [log])

    useEffect(() => {
        securityStateRef.current = {
            ...securityStateRef.current,
            challengeParam: encryptedParam,
            csrfToken: csrfToken,
            recaptchaSiteKey: recaptchaSiteKey
        };

        securityConfigRef.current = {
            ...securityConfigRef.current,
            debug: debug,
            production: production,
            // sitename: sitename
        };
    }, [encryptedParam, csrfToken, debug, production, recaptchaSiteKey]);


    return {
        // State
        ...state,

        // Data
        sitename,

        // Functions
        initFingerprint,
        onRecaptchaSuccess,
        onRecaptchaExpired,
        onRecaptchaError,
        updateFingerprintStatus,

        // Refs (for external access)
        securityStateRef,
        securityConfigRef
    };
}

const usePageEffects = (securityHookData) => {
    const {
        initFingerprint,
        onRecaptchaSuccess,
        onRecaptchaExpired,
        onRecaptchaError,
        updateFingerprintStatus,
        updateStatus,
        securityConfigRef,
        securityStateRef
    } = securityHookData;

    const initializeDOM = useCallback(async () => {
        try {
            await initFingerprint();
        } catch (error) {
            console.error('DOM initialization error:', error);
        }
    }, [initFingerprint]);

    const recaptchaRenderedRef = useRef(false);

    const injectRecaptchaScript = () => {
        if(document.getElementById('recaptcha-script')) return;
        const script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit';
        script.async = true;
        document.head.appendChild(script);

    };

    const setupRecaptcha = useCallback(() => {
        // if (typeof window !== 'undefined' && !window.onloadCallback) {
        //     window.onloadCallback = () => {
        //         setTimeout(() => {
        //             updateFingerprintStatus(SECURITY_STATUS.READY, '');

        //             if (window.grecaptcha && document.getElementById('recaptcha-container')) {
        //                 try {
        //                     window.grecaptcha.render('recaptcha-container', {
        //                         sitekey: securityStateRef.current.recaptchaSiteKey,
        //                         callback: onRecaptchaSuccess,
        //                         'expired-callback': onRecaptchaExpired,
        //                         'error-callback': onRecaptchaError
        //                     });
        //                 } catch (error) {
        //                     console.error('Error rendering reCAPTCHA:', error);
        //                 }
        //             }
        //         }, SECURITY_CONFIG.RECAPTCHA_RENDER_DELAY);
        //     };
        // }

        if(typeof window === 'undefined' || window == null) return;
        
        window.onloadCallback = () => {
            setTimeout(() => {
                updateFingerprintStatus(SECURITY_STATUS.READY, '');
                if(window.grecaptcha && document.getElementById('recaptcha-container') && !recaptchaRenderedRef.current) {
                    try {
                        window.grecaptcha.render('recaptcha-container', {
                            sitekey: securityStateRef.current.recaptchaSiteKey,
                            callback: onRecaptchaSuccess,
                            'expired-callback': onRecaptchaExpired,
                            'error-callback': onRecaptchaError
                        });
                        recaptchaRenderedRef.current = true;
                    } catch (error) {
                        console.error('Error rendering reCAPTCHA:', error);
                    }
                }
            }, SECURITY_CONFIG.RECAPTCHA_RENDER_DELAY);
        };

        
        if(window.__deviceCollected) {
            injectRecaptchaScript();
        } else {
            document.addEventListener('device-collected', injectRecaptchaScript, { once: true});
        }


    }, [onRecaptchaSuccess, onRecaptchaExpired, onRecaptchaError, updateFingerprintStatus, injectRecaptchaScript]);

    // Event handlers
    const handleVisibilityChange = useCallback(() => {
        if (!document.hidden && securityStateRef.current.recaptchaCompleted) {
            try {
                const response = window.grecaptcha?.getResponse();
                if (!response) {
                    securityStateRef.current.recaptchaCompleted = false;
                    updateStatus(SECURITY_STATUS.ERROR, 'Verification expired. Please complete again.');
                }
            } catch (e) {
                console.error('Error checking reCAPTCHA response:', e);
            }
        }
    }, [updateStatus]);

    const handlePageShow = useCallback((event) => {
        if (event.persisted) {
            window.location.reload();
        }
    }, []);

    const handleBeforeUnload = useCallback(() => {
        // Clean up sensitive data
        Object.assign(securityStateRef.current, {
            challengeParam: null,
            csrfToken: null,
            fingerprint: null
        });
    }, []);

    // DOM ready initialization
    useEffect(() => {
        const initialize = () => {
            initializeDOM();
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initialize);
        } else {
            initialize();
        }

        return () => {
            document.removeEventListener('DOMContentLoaded', initialize);
        };
    }, [initializeDOM]);

    // Setup reCAPTCHA and event listeners
    useEffect(() => {
        setupRecaptcha();

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            // Cleanup event listeners
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('beforeunload', handleBeforeUnload);

            document.removeEventListener('device-collected', injectRecaptchaScript);
            // Cleanup global callback
            if (window.onloadCallback) {
                delete window.onloadCallback;
            }

            // Remove reCAPTCHA script if exists
            const script = document.getElementById('recaptcha-script');
            if (script) script.remove();
        };
    }, [setupRecaptcha, handleVisibilityChange, handlePageShow, handleBeforeUnload, injectRecaptchaScript]);
};

const LoadingSpinner = ({ size = 12 }) => (
    <div className="lds-spinner">
        {[...Array(size)].map((_, i) => (
            <div key={i}></div>
        ))}
    </div>
);

const FingerprintStatus = ({ status, message }) => {
    if (status === SECURITY_STATUS.LOADING) {
        return (
            <div className="fingerprint-loading">
                <LoadingSpinner />
                <span>Verifying...</span>
            </div>
        );
    }

    if (status === SECURITY_STATUS.ERROR) {
        return (
            <div className="fingerprint-error">
                <span className="error-icon" aria-label="Error"></span>
                <span>{message || 'Failed to verify your browser'}</span>
            </div>
        );
    }

    return null;
};

const StatusText = ({ status, message }) => {
    if (!message) return null;

    const getStatusIcon = () => {
        switch (status) {
            case SECURITY_STATUS.SUCCESS:
                return <span className="success-check" aria-label="Success"></span>;
            case SECURITY_STATUS.ERROR:
                return <span className="error-icon" aria-label="Error"></span>;
            default:
                return null;
        }
    };

    return (
        <div
            className={`status-text ${status}`}
            id="status-text"
            role="status"
            aria-live="polite"
        >
            {getStatusIcon()}
            {status !== SECURITY_STATUS.LOADING && (
                <span>{message}</span>
            )}
        </div>
    );
};

const ChallengePage = () => {
    const data = useLoaderData();
    // Use security challenge hook
    const securityHookData = useSecurityChallenge(data);
    const {
        status,
        verificationStatus,
        fingerprintStatus,
        message,
        isLoading,
        sitename,
        securityConfigRef
    } = securityHookData;

    // Use page effects hook
    usePageEffects(securityHookData);

    return (
        <PageWrapper>
            <div className="challenge-container">
                <h1 className="site-name" id="site-name">
                    {sitename}
                </h1>
                <h2 className="main-heading">
                    Verifying you are human. This may take a few seconds.
                </h2>

                <div className="verification-section">
                    <div className="fingerprint-status" id="fingerprint-status">
                        <FingerprintStatus
                            status={fingerprintStatus}
                            message={message}
                        />
                    </div>

                    <div
                        className="recaptcha-container"
                        id="recaptcha-container"
                        aria-label="reCAPTCHA verification"
                    ></div>

                    <div className="verification-status" id="verification-status">
                        <StatusText
                            status={status}
                            message={message}
                        />
                    </div>
                </div>

                <div id="explain-message">
                    <span className="current-host">{sitename}</span> needs to review the security of your connection before proceeding.

                </div>
            </div>
        </PageWrapper>
    );
};



module.exports = ChallengePage;
